import { useGetOccupancyQuery } from '../../../../app/Slices/SevenDayApiSlice';
import {
    Card,
    CardContent,
    List,
    ListItemButton,
    ListItemSecondaryAction,
    ListItemText,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    PolarAngleAxis,
    RadialBar,
    RadialBarChart,
    ResponsiveContainer,
    Tooltip,
} from 'recharts';
import { Period } from '../../../enums/reports';
import { NavigateNext } from '@mui/icons-material';
import { ModuleOccupancy } from '../../../../app/Slices/SevenDayApiTypes';

const CustomTooltip = ({ data, ...props }: any) => {
    const focusedData = data?.[props?.label];

    return (
        <Card>
            <CardContent>
                <Typography sx={{ fontWeight: 'bold' }}>
                    {focusedData?.name}
                </Typography>
                <Typography>
                    {parseFloat(focusedData?.availableNightsOccupancy).toFixed(
                        1,
                    )}
                    % Occupancy
                </Typography>
            </CardContent>
        </Card>
    );
};

interface TopPerformersProps {
    period: Period;
}

const TopPerformers = ({ period }: TopPerformersProps) => {
    const theme = useTheme();
    const navigate = useNavigate();

    const { data } = useGetOccupancyQuery({ period });

    const handleNavigate = (experience: ModuleOccupancy) => {
        navigate(`/reports/occupancy/${experience.id}`);
    };

    const topPerformers = data?.slice(0, 5);

    return (
        <Card sx={{ height: '100%' }}>
            <CardContent>
                <Typography variant='subHeading'>Top Experiences</Typography>
                <Typography variant='subtitle2'>
                    Check out the latest stats and insights on your
                    top-performing listings for this month.
                </Typography>
                <Stack spacing={1}>
                    <ResponsiveContainer width='100%' height={200}>
                        <RadialBarChart
                            cy={200}
                            innerRadius='120%'
                            outerRadius='220%'
                            data={topPerformers}
                            startAngle={180}
                            endAngle={0}
                            margin={{ top: 40, left: 0, right: 0, bottom: 0 }}
                        >
                            <RadialBar
                                background
                                dataKey='availableNightsOccupancy'
                                onClick={(e) => handleNavigate(e)}
                                fill={theme.palette.textAccent.main}
                            />
                            <text
                                x='50%'
                                y='85%'
                                // scaleToFit={true}
                                textAnchor='middle'
                            >
                                <tspan
                                    x='50%'
                                    dy='0'
                                    style={{
                                        fontWeight: 'bold',
                                        fill: theme.palette.text.primary,
                                        fontFamily: theme.typography.fontFamily,
                                    }}
                                >
                                    {data?.[0]?.name}
                                </tspan>
                                <tspan
                                    x='50%'
                                    dy='1.2em'
                                    style={{
                                        fill: theme.palette.text.primary,
                                        fontFamily: theme.typography.fontFamily,
                                    }}
                                >
                                    {data?.[0]?.availableNightsOccupancy.toFixed(
                                        1,
                                    )}
                                    %
                                </tspan>
                            </text>
                            <Tooltip content={<CustomTooltip data={data} />} />
                            <PolarAngleAxis
                                tick={false}
                                domain={[0, 100]}
                                type='number'
                            />
                        </RadialBarChart>
                    </ResponsiveContainer>
                    <List>
                        {topPerformers?.map((experience, index: number) => (
                            <ListItemButton
                                key={index}
                                divider
                                onClick={() => handleNavigate(experience)}
                            >
                                <ListItemText
                                    primary={`#${index + 1}: ${
                                        experience.name
                                    }`}
                                    secondary={`${experience.bookings} reservations/s`}
                                />
                                <ListItemSecondaryAction>
                                    <NavigateNext />
                                </ListItemSecondaryAction>
                            </ListItemButton>
                        ))}
                    </List>
                </Stack>
            </CardContent>
        </Card>
    );
};

export default TopPerformers;
